import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { Button } from '@/common/components/button/Button';
import { SignUpData } from '../SignUp';

/**
 * 4단계: 회원가입 완료 결과 컴포넌트
 */

interface ResultStepProps {
  data: SignUpData;
}

const ResultStep: React.FC<ResultStepProps> = ({ data }) => {
  const [countdown, setCountdown] = useState(10);

  /**
   * 마이스페이스로 이동 (신규 회원은 튜토리얼 표시)
   */
  const handleGoToMySpace = () => {
    window.location.href = '/my-space?tutorial=true';
  };

  /**
   * 컴포넌트 마운트 시 회원가입 API 호출
   */
  useEffect(() => {
    const performSignUp = async () => {
      try {
        // TODO: 실제 회원가입 API 호출
        console.log('회원가입 데이터:', data);
        
        // const result = await authService.signUp({
        //   name: data.name,
        //   phone: data.phone,
        //   birthGender: data.birthGender,
        //   userId: data.userId,
        //   password: data.password,
        //   termsAgreed: data.termsAgreed,
        //   privacyAgreed: data.privacyAgreed,
        //   marketingAgreed: data.marketingAgreed,
        // });
        
        // 임시로 성공 처리
        console.log('회원가입 완료');
      } catch (error) {
        console.error('회원가입 실패:', error);
        alert('회원가입 중 오류가 발생했습니다. 다시 시도해주세요.');
      }
    };

    performSignUp();
  }, [data]);

  /**
   * 10초 카운트다운 및 자동 이동
   */
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          handleGoToMySpace();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <Container>      
      <Header>
        <Subtitle>
          <Strong>{data.name}</Strong>님, 팩트시트에 오신 것을 환영합니다!
        </Subtitle>
      </Header>

      <AccountInfo>
        <InfoList>
          <InfoItem>
            <InfoLabel>아이디</InfoLabel>
            <InfoValue>{data.userId}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>이름</InfoLabel>
            <InfoValue>{data.name}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>휴대폰</InfoLabel>
            <InfoValue>{data.phone}</InfoValue>
          </InfoItem>
        </InfoList>
      </AccountInfo>

      <ServiceInfo>
        <ServiceTitle>서비스 이용 안내</ServiceTitle>
        <ServiceDescription>
          현재 개인 계정으로 생성이 되었습니다.<br/>
          팩트시트의 서비스는 <ServiceHighlight>스타트업, 투자사, 액셀러레이팅 프로그램</ServiceHighlight>의 공간을 제공하고 있습니다.
        </ServiceDescription>
        
        <StepList>
          <StepItem>
            <StepNumber>1</StepNumber>
            <StepContent>
              <StepTitle>이메일 인증</StepTitle>
              <StepDesc>각 서비스를 이용하시려면 이메일 인증을 완료해주세요</StepDesc>
            </StepContent>
          </StepItem>
          <StepItem>
            <StepNumber>2</StepNumber>
            <StepContent>
              <StepTitle>회사 관리</StepTitle>
              <StepDesc>기업을 만들고 공간 서비스를 이용하실 수 있습니다</StepDesc>
            </StepContent>
          </StepItem>
        </StepList>
      </ServiceInfo>

      <ButtonContainer>
        <Button
          $variant="gradient"
          $size="md"
          $isFull
          onClick={handleGoToMySpace}
        >
          마이스페이스로 이동
        </Button>
        <CountdownText>
          {countdown}초 후 자동으로 이동합니다
        </CountdownText>
      </ButtonContainer>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  text-align: center;
`;

const SuccessIcon = styled.div`
  font-size: 64px;
  margin: 16px 0;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Title = styled.h2`
  ${typography.heading.h2}
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const Subtitle = styled.p`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.secondary};
  margin: 0;
  line-height: 1.5;
`;

const Strong = styled.strong`
  color: ${semanticColors.background.brand};
  font-weight: 600;
`;

const WelcomeMessage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, ${semanticColors.background.tertiary} 0%, rgba(232, 242, 255, 0.3) 100%);
  border-radius: 8px;
  text-align: left;
`;

const MessageTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin: 0;
  text-align: center;
`;

const FeatureList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const FeatureItem = styled.li`
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.secondary};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AccountInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
`;

const InfoTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin: 0;
  text-align: center;
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background-color: ${semanticColors.background.primary};
  border-radius: 6px;
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InfoLabel = styled.span`
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.secondary};
`;

const InfoValue = styled.span`
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.primary};
  font-weight: 500;
`;

const ServiceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, ${semanticColors.background.tertiary} 0%, rgba(232, 242, 255, 0.3) 100%);
  border-radius: 12px;
  text-align: left;
`;

const ServiceTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin: 0;
  text-align: center;
`;

const ServiceDescription = styled.p`
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.secondary};
  margin: 0;
  line-height: 1.6;
  text-align: center;
`;

const ServiceHighlight = styled.span`
  color: ${semanticColors.background.brand};
  font-weight: 600;
`;

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
`;

const StepItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const StepNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: ${semanticColors.background.brand};
  color: ${semanticColors.text.white};
  border-radius: 50%;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
`;

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StepTitle = styled.div`
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.primary};
  font-weight: 500;
`;

const StepDesc = styled.div`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  line-height: 1.4;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const CountdownText = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
  margin: 0;
  text-align: center;
`;

export default ResultStep;
